/**
 * The give box after the donor authorizes (GH-847): thanks, the server's total, and the honest
 * state of the money — "received" only once the processor has confirmed it (SPEC.md §5.4), and
 * "processing" with a plain "you don't need to give again" until then.
 *
 * The host page is told once, by `useCompletion`: status and amount, nothing about the donor.
 */
import { cardFailureMessage, formatMoney, type MoneyWire } from "@givehere/contracts";
import { useState } from "react";
import { SYNTHETIC_PAYMENTS, confirmSyntheticPayment } from "../api.js";
import { useCompletion } from "./embed-hooks.js";

export function EmbedThanks({
  paid,
  email,
  organizationName,
  total,
  onAgain,
}: {
  paid: { readonly donationId: string; readonly accessToken: string };
  email: string;
  organizationName: string;
  total: MoneyWire;
  onAgain: () => void;
}) {
  const { status, receiptSent, receiptAwaitingCharge } = useCompletion(paid, email);
  const [simulated, setSimulated] = useState(false);
  const shown = status?.total ?? total;
  const state = status?.displayStatus ?? "processing";

  return (
    <main className="embed embed__thanks" aria-live="polite">
      <h1 className="embed__org">Thank you</h1>
      <p className="embed__thanks-amount">{formatMoney(shown)}</p>
      <p className="embed__note" dir="auto">
        to {organizationName}
      </p>

      {state === "received" && (
        <p className="embed__note">
          Received.
          {receiptSent ? " Your receipt is on its way to your email." : ""}
        </p>
      )}
      {state === "processing" && status?.heldForEndOfDay && (
        <p className="embed__note">
          Approved. <bdi>{organizationName}</bdi> charges gifts at the end of the day — you
          don&apos;t need to give again.
          {receiptAwaitingCharge ? " Your receipt will be emailed when it is charged." : ""}
        </p>
      )}
      {state === "processing" && !status?.heldForEndOfDay && (
        <p className="embed__note">
          Processing — you don&apos;t need to give again. Your bank will show the charge shortly.
        </p>
      )}
      {state === "failed" && (
        <p className="embed__error" role="alert">
          {status?.failureReason
            ? cardFailureMessage(status.failureReason, "donor")
            : "The payment did not go through. Nothing was taken."}
        </p>
      )}

      {SYNTHETIC_PAYMENTS && state === "processing" && !simulated && (
        <button
          type="button"
          className="embed__secondary"
          onClick={() => {
            setSimulated(true);
            void confirmSyntheticPayment(paid.donationId, paid.accessToken);
          }}
        >
          Simulate the bank confirming (test build)
        </button>
      )}

      <button type="button" className="embed__secondary" onClick={onAgain}>
        Make another gift
      </button>
    </main>
  );
}
